/**
 * Formatação pt-BR em um lugar só. Os `Intl.*Format` são caros de construir,
 * por isso ficam como constantes de módulo em vez de serem recriados a cada
 * célula de tabela renderizada.
 */

const DATA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'America/Sao_Paulo' });

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});

export function formatarData(iso: string): string {
  return DATA.format(new Date(iso));
}

export function formatarDataHora(iso: string): string {
  return DATA_HORA.format(new Date(iso));
}

/**
 * Formata uma data sem hora, `AAAA-MM-DD`, como ela foi escrita.
 *
 * `new Date('2026-09-01')` é meia-noite em UTC, e meia-noite em UTC é o dia 31
 * de agosto em Brasília — um período pedido de 1º de setembro apareceria
 * começando em 31 de agosto. Data sem hora não tem fuso para converter: é um
 * dia do calendário, e o que a tela mostra é o que foi digitado.
 */
export function formatarDataSimples(data: string): string {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
  return partes === null ? data : `${partes[3]}/${partes[2]}/${partes[1]}`;
}

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * O servidor manda centavos inteiros, e é assim que o valor atravessa a rede:
 * dividir por 100 é a última coisa que acontece, só para exibir.
 */
export function formatarMoedaDeCentavos(centavos: number): string {
  return MOEDA.format(centavos / 100);
}

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * Contagem com o ponto de milhar. Serve para o que se lê como quantidade —
 * "25.431 notas analisadas"; o número da nota continua saindo cru, que é como
 * ele é digitado na busca e impresso no cupom.
 */
export function formatarInteiro(valor: number): string {
  return INTEIRO.format(valor);
}

const QUANTIDADE = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

/**
 * Quantidade do item da nota. O XML traz `1.0000` para um pacote e `0.584`
 * para o que é vendido a peso, e o cupom mostra os dois como o PDV mostrou.
 */
export function formatarQuantidade(valor: number): string {
  return QUANTIDADE.format(valor);
}

/** Chave de acesso em blocos de quatro, que é como ela é conferida. */
export function formatarChaveAcesso(chave: string): string {
  return chave.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/**
 * Os meses por extenso, para o combo de competência.
 *
 * Lista escrita e não `Intl.DateTimeFormat`: aqui o nome é rótulo de uma
 * opção, não a formatação de uma data que exista em algum lugar — montar um
 * `Date` fictício só para extrair "setembro" de cada um é dar a volta no
 * mundo para dizer o que já se sabe.
 */
export const MESES_DO_ANO = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** Competência `AAAA-MM` por extenso: `setembro de 2026`. */
export function competenciaPorExtenso(competencia: string): string {
  const [ano, mes] = competencia.split('-');
  const nome = MESES_DO_ANO[Number(mes) - 1];
  return nome === undefined || ano === undefined
    ? competencia
    : `${nome.toLowerCase()} de ${ano}`;
}

/** Competência `AAAA-MM` como `MM/AAAA`, que é como o contador lê. */
export function formatarCompetencia(competencia: string): string {
  const [ano, mes] = competencia.split('-');
  return mes === undefined ? competencia : `${mes}/${ano}`;
}

export function formatarCnpj(cnpj: string): string {
  if (cnpj.length !== 14) return cnpj;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

/** Só dígitos: a tela de cadastro é mais restritiva que o domínio. */
export function somenteDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

/** Aplica a máscara `00.000.000/0000-00` conforme se digita. */
export function mascararCnpj(entrada: string): string {
  const limpo = somenteDigitos(entrada).slice(0, 14);

  const partes = [
    limpo.slice(0, 2),
    limpo.slice(2, 5),
    limpo.slice(5, 8),
    limpo.slice(8, 12),
    limpo.slice(12, 14),
  ];

  let resultado = partes[0] ?? '';
  if (partes[1]) resultado += `.${partes[1]}`;
  if (partes[2]) resultado += `.${partes[2]}`;
  if (partes[3]) resultado += `/${partes[3]}`;
  if (partes[4]) resultado += `-${partes[4]}`;

  return resultado;
}

/** Ignora acentos e caixa, para "comercio" encontrar "Comércio". */
export function normalizarBusca(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
